import { Component, Input, Output, ViewChild } from '@angular/core';
import { AbstractControl,  NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';

import { Subject } from 'rxjs';
import dayjs, { Dayjs } from 'dayjs/esm';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { DaterangepickerDirective, LocaleConfig } from 'ngx-daterangepicker-material';
import { EventEmitter } from '@angular/core';
import { DateRange, DateRanges, TimePeriod } from 'ngx-daterangepicker-material/daterangepicker.component';
@Component({
  selector: 'app-datetime-range',
  templateUrl: './datetime-range.component.html',
  styleUrls: ['./datetime-range.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: DatetimeRangeComponent
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: DatetimeRangeComponent
    }
  ]
})
export class DatetimeRangeComponent {

  selectedRange: object;

  @ViewChild(DaterangepickerDirective, { static: false }) pickerDirective: DaterangepickerDirective;

  @Input() appearance: MatFormFieldAppearance = 'outline';

  @Input() label: string | null = null;

  @Input() autoApply: boolean = true;

  @Input() closeOnAutoApply: boolean = true;

  @Input() linkedCalendars: boolean = true;

  @Input() minDate: Dayjs; // set the minimal and maximal date, these options are either a dayjs date or string in ISO format

  @Input() maxDate: Dayjs; // set the minimal and maximal date, these options are either a dayjs date or string in ISO format  

  @Input() showDropdowns: boolean = true;

  @Input() showWeekNumbers: boolean = false;

  @Input() showISOWeekNumbers: boolean = false;

  @Input() showClearButton: boolean = false;

  @Input() showCancel: boolean = false;

  @Input() dateLimit: number;

  @Input() placeholder: string = '';

  // Range
  @Input() showCustomRangeLabel: boolean = false;

  @Input() alwaysShowCalendars: boolean = false; // set to true if you want to display the ranges with the calendar

  @Input() keepCalendarOpeningWithRange: boolean = false; // set to true if you want the calendar won't be closed after choosing a range

  @Input() showRangeLabelOnInput: boolean = false; // set to true if you want to display the range label on input

  @Input() customRangeDirection: boolean = false; // set to true if you want to allow selection range from end date first

  @Input() lockStartDate: boolean = false; // set to true if you want to lock start date and change only the end date

  // Timepicker  
  @Input() timePicker: boolean = false; // set the attribute timePicker to true if you want to enable the timepicker.

  @Input() timePicker24Hour: boolean = false; // set to true if you want to set the timepicker to 24h instead of having AM and PM

  @Input() timePickerIncrement: number = 1; // set the value increment of the minutes (eg: for 12 there would be 0mn, 15mn, 30mn, 45mn,)

  @Input() timePickerSeconds: boolean = false; // set true if you want do display second's select

  // Customisation
  @Input() firstMonthDayClass: string; // add a custom class for all first day of the month

  @Input() lastMonthDayClass: string; // add a custom class for all last day of the month

  @Input() emptyWeekRowClass: string; // add a custom class in a row with a date in a week not in the current month

  @Input() emptyWeekColumnClass: string; // add a custom class for all date in a week not in the current month

  @Input() lastDayOfPreviousMonthClass: string = 'last-previous-day'; // add a custom class for the last day of the previous month

  @Input() firstDayOfNextMonthClass: string = 'first-next-day'; // add a custom class for the first day of the next month

  // Positioning
  @Input() opens: 'left' | 'center' | 'right' = 'right'; // position the calendar from the input element

  @Input() drops: 'up' | 'down' = 'down'; // position the calendar to the up or down of the calendar

  @Output() valueChanged = new Subject<TimePeriod>();

  @Output() clearClicked = new Subject<boolean>();

  @Output() rangeClicked = new Subject<DateRange>();
  @Output() datePassed: EventEmitter<any> = new EventEmitter();
  @Output() rangeSelected: EventEmitter<any> = new EventEmitter();
  locale: LocaleConfig = {
    format: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
    displayFormat: 'D MMM, YYYY  HH:mm:ss',
    separator: '   -   ',
    cancelLabel: 'Cancel',
    applyLabel: 'Apply',
    clearLabel: 'Clear',
    customRangeLabel: 'Custom Range'
  };

  // Preset
  ranges: DateRanges = {
    ['Today']: [dayjs().startOf('days'), dayjs()],
    ['Yesterday']: [dayjs().subtract(1, 'days').startOf('days'), dayjs().subtract(1, 'days').endOf('days')],
    ['Last 7 Days']: [dayjs().subtract(6, 'days').startOf('days'), dayjs()],
    ['Last 30 Days']: [dayjs().subtract(29, 'days').startOf('days'), dayjs()],
    ['This Month']: [dayjs().startOf('month'), dayjs()],
    ['Last Month']: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')],
    ['Last 3 Month']: [dayjs().subtract(3, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')]
  };

  // Control Value Accessor
  touched = false;
  disabled = false;
  validationStatus: ValidationErrors | null = null;
  value: TimePeriod;
  constructor() { }

  onChange: Function = Function;

  onTouched = () => { };

  registerOnChange(onChange: any) {
    this.onChange = () => onChange(this.value);
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  writeValue(value: TimePeriod) {
    this.value = value;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    this.validationStatus = null;
    return this.validationStatus;
  }

  // Events
  onRangeClicked(value: DateRange) {
    this.rangeClicked.next(value);
    this.selectedRange = value;
  }

  onDatesUpdated(value: any) {
    if (value?.startDate && value?.endDate) {
      this.valueChanged.next(value);
      this.value = value;
      this.datePassed.emit(value);
      this.onChange(this.value);
      this.rangeSelected.emit(this.selectedRange);
    }
  }

  onClearClicked() {
    this.clearClicked.next(true);
  }

  onIconClick(event?: Event) {
    this.pickerDirective.open(event);
  }

}
